import isEqual from 'lodash/isEqual';
import type { FabricEditor } from '../core/fabric-editor';
import type { EditorPlugin } from '../interfaces/editor-plugin';
import { FabricPluginName } from './plugin-name';

export interface HistoryFacade {
  undo(): void;
  redo(): void;
  canUndo(): boolean;
  canRedo(): boolean;
  clear(): void;
  size(): number;
  index(): number;
  /**
   * Run `fn` with history recording suspended, then restore the previous
   * recording state. Use this to wrap programmatic, non-user re-derivation of
   * objects (e.g. placeholder refit/swap) so it does not create phantom undo
   * steps. Re-entrant: nested transactions are safe.
   */
  transaction<T>(fn: () => T): T;
  /**
   * Run `fn` with intermediate snapshots suspended and record exactly one
   * snapshot at the end — one undo step for the whole `fn`. Use to wrap a
   * user-driven multi-field change (e.g. pasting a style onto a selection).
   * Re-entrant: nested `transaction`/`batch` calls only move the suspend
   * counter; the single snapshot is written when the outermost `batch`
   * completes (a no-op `fn` records nothing thanks to the isEqual guard).
   */
  batch<T>(fn: () => T): T;
  /**
   * Replace the canvas with `state` and record the result as exactly **one**
   * new undo step — a forward move, not a rewind: whatever was on the canvas
   * stays behind it, so a single `undo()` brings it all back.
   *
   * This is what a consumer needs to revert to an externally-owned state (e.g.
   * "discard my unsaved changes" restores the last persisted template) while
   * keeping that revert itself undoable. Rewinding with repeated `undo()`
   * cannot express it: it leaves the cursor at the bottom of the stack, where
   * `undo()` has nothing left to do and only `redo()` would bring the work
   * back.
   *
   * Recording stays suspended across the (possibly async) load and the
   * `canvas:loaded` emit, exactly like undo/redo, so consumers' refit-on-load
   * handlers do not split the step. `done` fires after the step is recorded.
   * An identical state records nothing.
   */
  replaceState(state: { version?: string; objects: unknown[] }, done?: () => void): void;
}

declare module '../core/fabric-editor' {
  interface FabricEditor {
    history?: HistoryFacade;
  }
}

type Snapshot = Record<string, unknown>;

const DEFAULT_PROPERTIES_TO_INCLUDE = [
  'data',
  'selectable',
  'evented',
  'lockMovementX',
  'lockMovementY',
  'lockScalingX',
  'lockScalingY',
  'lockRotation',
  'hasControls',
  'hoverCursor',
];

export interface HistoryPluginOptions {
  limit?: number;
  propertiesToInclude?: string[];
}

export function historyPlugin(options: HistoryPluginOptions = {}): EditorPlugin {
  const limit = options.limit ?? 100;
  const propertiesToInclude = Array.from(
    new Set([...DEFAULT_PROPERTIES_TO_INCLUDE, ...(options.propertiesToInclude ?? [])]),
  );

  return {
    name: FabricPluginName.HISTORY,
    install(editor: FabricEditor) {
      const stack: Snapshot[] = [snapshot(editor, propertiesToInclude)];
      let cursor = 0;
      // Re-entrant suspend counter (replaces a boolean `restoring` flag). While
      // > 0, object lifecycle events do not record snapshots. Used both for the
      // restore round-trip and for `transaction(...)`.
      let suspend = 0;

      const emit = () => {
        editor.emit('history:changed', {
          canUndo: cursor > 0,
          canRedo: cursor < stack.length - 1,
          index: cursor,
          size: stack.length,
        });
      };

      const record = () => {
        if (suspend > 0) return;
        const next = snapshot(editor, propertiesToInclude);
        if (isEqual(stack[cursor], next)) return;
        // truncate forward history
        stack.length = cursor + 1;
        stack.push(next);
        if (stack.length > limit) {
          stack.shift();
        } else {
          cursor++;
        }
        emit();
      };

      const restore = (data: Snapshot, done: () => void) => {
        suspend++;
        editor.canvas.loadFromJSON(data, () => {
          editor.canvas.renderAll();
          // Keep recording suspended across the canvas:loaded emit: consumers
          // (e.g. the placeholder plugin) re-derive objects in response to it,
          // and those swaps must not be recorded as new undo steps.
          editor.emit('canvas:loaded', { source: 'history' });
          suspend--;
          done();
        });
      };

      const facade: HistoryFacade = {
        undo() {
          if (cursor <= 0) return;
          cursor--;
          restore(stack[cursor], emit);
        },
        redo() {
          if (cursor >= stack.length - 1) return;
          cursor++;
          restore(stack[cursor], emit);
        },
        canUndo: () => cursor > 0,
        canRedo: () => cursor < stack.length - 1,
        clear() {
          stack.length = 0;
          stack.push(snapshot(editor, propertiesToInclude));
          cursor = 0;
          emit();
        },
        size: () => stack.length,
        index: () => cursor,
        transaction<T>(fn: () => T): T {
          suspend++;
          try {
            return fn();
          } finally {
            suspend--;
          }
        },
        batch<T>(fn: () => T): T {
          suspend++;
          try {
            return fn();
          } finally {
            suspend--;
            if (suspend === 0) record();
          }
        },
        replaceState(state, done) {
          // `restore` keeps recording suspended for the whole load; `record`
          // then writes the single step (it snapshots the canvas *after* the
          // canvas:loaded re-derivations, and its isEqual guard makes a
          // no-op replace record nothing).
          restore(state as Snapshot, () => {
            record();
            done?.();
          });
        },
      };

      editor.history = facade;

      const offAdded = editor.on('object:added', record);
      const offModified = editor.on('object:modified', record);
      const offRemoved = editor.on('object:removed', record);

      // Stash unsubscribers on the plugin closure for dispose.
      (facade as HistoryFacade & { __off?: () => void }).__off = () => {
        offAdded();
        offModified();
        offRemoved();
      };
    },
    dispose(editor: FabricEditor) {
      const facade = editor.history as (HistoryFacade & { __off?: () => void }) | undefined;
      facade?.__off?.();
      editor.history = undefined;
    },
  };
}

function snapshot(editor: FabricEditor, propertiesToInclude: string[]): Snapshot {
  const json = editor.canvas.toJSON(propertiesToInclude) as Record<string, unknown> & {
    objects?: unknown[];
    version?: string;
  };
  return { version: json.version, objects: json.objects ?? [] };
}
